import React from 'react';
import { SEOHead, getLocalBusinessSchema } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { LeadForm } from '../components/common/LeadForm';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { generateWhatsAppLink, getGeneralWhatsAppMessage } from '../lib/utils';

export default function ContactPage() {
  const whatsappLink = generateWhatsAppLink(getGeneralWhatsAppMessage());

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      content: ['Ahmedabad, Gujarat', 'India - 380001'],
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: ['+91 98765 43210', '+91 79 1234 5678'],
      links: ['tel:+919876543210', 'tel:+917912345678'],
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: ['info@passionofaroma.com', 'sales@passionofaroma.com'],
      links: ['mailto:info@passionofaroma.com', 'mailto:sales@passionofaroma.com'],
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: ['Monday - Saturday', '10:00 AM - 7:00 PM'],
    },
  ];

  return (
    <>
      <SEOHead
        title="Contact Us - Get in Touch"
        description="Contact Passion of Aroma for wholesale fragrance inquiries. Visit our Ahmedabad office or reach us via phone, email, or WhatsApp."
        canonical="https://passionofaroma.com/contact"
        schemaData={getLocalBusinessSchema()}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0B1F3B]" data-testid="contact-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'Contact' }]} />
          <div className="mt-6 max-w-2xl">
            <span className="label-text mb-4 block">Get in Touch</span>
            <h1 className="heading-xl text-white mb-4">Contact Us</h1>
            <p className="text-gray-300 text-lg">
              Ready to partner with us? Have questions about our products? 
              We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="heading-md mb-8">Contact Information</h2>
              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#0B1F3B] flex items-center justify-center flex-shrink-0">
                      <info.icon className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0B1F3B] mb-1">{info.title}</h3>
                      {info.content.map((line, i) => (
                        info.links ? (
                          <a
                            key={i}
                            href={info.links[i]}
                            className="block text-gray-600 hover:text-[#D4AF37] transition-colors"
                          >
                            {line}
                          </a>
                        ) : (
                          <p key={i} className="text-gray-600">{line}</p>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp */}
              <div className="mt-8 p-6 bg-white">
                <h3 className="font-medium text-[#0B1F3B] mb-3">Quick Response via WhatsApp</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Get instant responses to your queries on WhatsApp
                </p>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-medium hover:bg-[#22c55e] transition-colors"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle className="h-5 w-5" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 bg-white p-8 lg:p-12">
              <LeadForm
                sourcePageUrl="/contact"
                title="Send us a Message"
                subtitle="Fill out the form below and we'll get back to you within 24 hours"
                showMessage={true}
                showBuyerType={true}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-96 bg-gray-200" data-testid="contact-map">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d235014.2926389458!2d72.41493127868626!3d23.02047846919327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e848aba5bd449%3A0x4fcedd11614f6516!2sAhmedabad%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1641234567890!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Our Location"
        />
      </section>
    </>
  );
}
